<template>
    <td
        :rowspan="rowSpan"
        :class="type + (cssClass !== '' ? ' ' + cssClass : '')"
    >
        <toggler :readonly="readonly">
            <!-- view (grouped) -->
            <template #activator="{ onClick }">
                <GroupedTableCell
                    v-if="groupValuesMap"
                    :key="'group_' + groupedValuesKey"
                    :groups="groupedValues"
                    @onEdit="onClick()"
                />
                <div v-else @click="onClick">{{cellContent}}</div>
            </template>

            <!-- select -->
            <template #deactivator="{ onHide }">
                <v-autocomplete
                    v-model="selectValue"
                    :key="'select_' + type"
                    :items="allValues"
                    item-value="id"
                    item-text="name"
                    item-color="var(--sc-blue-dark)"
                    :multiple="true"
                    :menu-props="{maxHeight: 800}"
                    v-on:blur="onHide"
                    @focusout="changeSelection"
                    :readonly="readonly"
            >
                <template v-slot:prepend-item>
                    <v-list-item
                        @click="selectAllToggle"
                    >
                        <v-list-item-action>
                            <v-icon color="var(--sc-blue-dark)">{{ icon }}</v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title><strong>Select All</strong></v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <v-divider
                        slot="prepend-item"
                        class="mt-2"
                    />
                </template>
            </v-autocomplete>
            </template>
        </toggler>
        <div v-if="!readonly && showAddRow" class="addRowPlus" :type="type" @click="addRow"/>
    </td>
</template>

<script>
    import Toggler from "../Toggler";
    import GroupedTableCell from "../Table/GroupedTableCell.vue";
    import i18n from "../../i18n";
    export default {
        name: "TagsGroupedSelectTableCell",
        components: {GroupedTableCell, Toggler},
        props: {
            type:            String,
            allValuesObject: Object,
            allValuesArray:  Array,
            value: {
                type: String,
                validator: function(value) {
                    return (typeof value === 'string') || value === null;
                }
            },

            codeTexts: {
                type:    [Boolean,String],
                default: false
            },
            groupValuesMap: {
                type:    Object,
                validator: function(value) {
                    return (typeof value === 'object') || value === null;
                },
                default: null
            },
            cssClass: {
                type: String,
                default: ''
            },
            rowSpan: {
                type:    Number,
                default: 1
            },
            showAddRow: {
                type:    Boolean,
                default: false
            },
            addEmptyOption: {
                type:    Boolean,
                default: false
            },
            emptyOptionText: {
                type:    String,
                default: ''
            },
            readonly: {
                type:    Boolean,
                default: false,
            },
        },
        data() {
            return {
                currentValue:        this.value,
                lineBreak:           '\n',
                specialLineBreaks:   {
                    inspectionCriteria: ', '
                },
                onHide:              false,
                allValuesArrayLocal: this.allValuesArray,
                loading:             false,
                removedValue:        ''
            }
        },
        mounted() {
            if (
                typeof this.value === 'string' // specific multiple values are selected (not all values = null)
                && this.value !== '' // skip empty values
            ) {
                this.checkValueAvailabilityAndPopulateRemovedValue(this.value.split(','));
            }
        },
        computed: {
            valueCount() {
                return Object.keys(this.allValuesObject).length;
            },
            selectValue: {
                // getter
                get: function () {
                    if (this.currentValue === null) {
                        return this.allValueKeysAsArray;
                    } else if (this.currentValue === '') {
                        return [];
                    } else {
                        return this.currentValue.split(',');
                    }
                },
                // setter
                set: function (newValue) {
                    if (newValue === '') newValue = [];
                    newValue = newValue.filter(el => el);
                    if (newValue.length === this.valueCount) {
                        // null => all values
                        this.currentValue = null
                    } else {
                        this.currentValue = newValue.join(',');
                    }
                }
            },
            /* Returns an object of value array grouped by property groupValuesMap */
            groupedValues() {
                let values;
                if (this.currentValue === null) {
                    values = this.allValueKeysAsArray;
                } else if (this.currentValue === '') {
                    values = [];
                } else {
                    values = this.currentValue.split(',');
                }

                let groupedValues     = {};
                let groupedValueCount = {};
                let groupName;
                // count values for header count display
                this.allValueKeysAsArray.forEach(value => {
                    if (this.type === 'locationCode') {
                        groupName = this.groupValuesMap[value.substring(0,1)];
                    } else {
                        groupName = this.groupValuesMap[value];
                    }
                    if (typeof groupedValueCount[groupName] === 'undefined') {
                        groupedValueCount[groupName] = 0;
                    }
                    groupedValueCount[groupName]++;

                    if (values.includes(value)) {
                        if (typeof groupedValues[groupName] === 'undefined') {
                            groupedValues[groupName] = [];
                        }
                        groupedValues[groupName].push(this.getElementText(value))
                    }
                });
                groupedValues.__counts = groupedValueCount;

                return groupedValues;
            },
            groupedValuesKey() {
                let key;
                if (this.currentValue === null) {
                    key = Object.keys(this.allValueKeysAsArray).join(',');
                } else {
                    key = this.currentValue;
                }
                return key;
            },
            icon() {
                if (this.currentValue === '') {
                    // none selected
                    return 'mdi-checkbox-blank-outline'; // empty checkbox
                } else if (this.currentValue === null) {
                    // all selected
                    return 'mdi-checkbox-marked'; // checked checkbox
                } else {
                    // some selected
                    return 'mdi-minus-box'; // checkbox with minus
                }
            },
            /* Returns the cell content depending column type and special line break  */
            cellContent() {
                let values;
                if (this.currentValue === null) {
                    return i18n.t('-- all values --');
                } else if (this.currentValue === '') {
                    return i18n.t('-- please select --')
                } else {
                    values = this.currentValue.split(',');
                }

                let returnValues = [];

                for (let index in values) {
                    returnValues.push(this.getElementText(values[index]));
                }

                if (returnValues.length === 0) {
                    returnValues = [i18n.t('-- please select --')];
                }

                return returnValues.join(this.getLineBreak(this.type));
            },
            allValues() {
                let allValues = this.allValuesArray;
                if (this.emptyOptionText !== '') {
                    allValues.splice(0, 0, {
                        id:       '',
                        name:     this.emptyOptionText,
                        disabled: !this.addEmptyOption,
                    })
                }

                return allValues;
            },
            allValueKeysAsArray()
            {
                // convert object array to key string array
                // filter group header with empty value
                return this.allValuesArrayLocal.map(item => item.id).filter(item => item);
            }
        },
        methods: {
            addRow()             { if (!this.readonly) this.$emit('addRow'); },
            changeSelection()    { if (!this.readonly) this.$emit('changeSelection', this.selectValue); },
            setLoading(state)    { this.loading = !!state; },
            getLineBreak(column) { return this.specialLineBreaks[column] || this.lineBreak; },

            selectAllToggle() {
                if (this.readonly) {
                    return;
                }
                // direction: some -> all -> none -> all -> none ...
                if (this.currentValue !== null) {
                    this.currentValue = null;
                } else {
                    this.currentValue = '';
                }

                this.$emit('changeSelection', this.currentValue);
            },

            /* Returns the text of value depending on column type, codeTexts parameter and existence of allValuesObject */
            getElementText(value) {
                let text;
                if (this.allValuesObject) {
                    if (this.codeTexts === true) {
                        text = this.allValuesObject[value];
                        if (
                            this.type !== 'serviceType'
                            && this.type !== 'surveyType'
                            && this.type !== 'repairArea'
                            && this.type !== 'inspectionCriteria'
                        ) {
                            text = value + ' - ' + text;
                        }
                    } else if (this.codeTexts === 'label') {
                        text = this.allValuesObject[value];
                    } else {
                        if (
                            this.type !== 'serviceType'
                            && this.type !== 'surveyType'
                            && this.type !== 'repairArea'
                            && this.type !== 'inspectionCriteria'
                        ) {
                            text = value;
                        } else {
                            text = this.allValuesObject[value];
                        }
                    }
                } else {
                    text = value;
                }

                return text;
            },
            checkValueAvailabilityAndPopulateRemovedValue(values)
            {
                let removedValues = [];
                values.forEach(key => {
                    if (!this.allValueKeysAsArray.includes(key)) {
                        removedValues.push(key);
                    }
                });
                this.removedValue = removedValues.join(',');
            },
            updateSelectableEntries(data)
            {
                if (this.readonly) {
                    return [];
                }

                // if current value is not selectable anymore
                // and some values are selected (not all and not none)
                // unset current value
                if (
                    this.currentValue !== null
                    && this.currentValue !== ''
                ) {
                    let currentValue = this.currentValue.split(',');
                    let newValue = currentValue;
                    currentValue.forEach((value) => {
                        if (!data.includes(value)) {
                            newValue.splice(currentValue.indexOf(value), 1);
                        }
                    });
                    this.currentValue = newValue.join(',');
                }

                // convert selectable entries to value object array
                let selectableObjectArray = [];
                data.forEach(value => {
                    selectableObjectArray.push({
                        id: value,
                        name: this.getElementText(value)
                    });
                });
                this.allValuesArrayLocal = selectableObjectArray;
            }
        }
    }
</script>

<style scoped>
    div.editor-box table.vcs-table td.ellipsis {
        white-space: pre-line;
    }
    div.editor-box table.vcs-table td.ellipsis div.ellipsis {
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
        white-space: nowrap;
    }
</style>